import useSWR from "swr";
import { Utils } from "@/common/utils";
import { StandardResponse } from "@/types/swr";
import { Ticket } from "Stores/dashboardStore/tickets";
const clientConfigs = Utils.getEAIClientConfigs();

export type DueAndUpcomingActionPlan = {
    as_due_type: string
    count: number
}

export type DueAndUpcomingActionPlansResponse = {
    data: DueAndUpcomingActionPlan[]
}

export function useDueAndUpcomingActionPlans(actionPlanFilter = "myItems") {
    const { data, error, isLoading, mutate } = useSWR<DueAndUpcomingActionPlansResponse, Error>(`${clientConfigs.endpoint}/schools/${clientConfigs.school}/actionplans/due-and-upcoming?filter=${actionPlanFilter}`);

    return {
        data: data ? data.data.sort((a, b) => a.as_due_type === "overdue" ? -1 : b.as_due_type === "overdue" ? 1 : a.as_due_type.localeCompare(b.as_due_type)) : undefined,
        error,
        isLoading,
        mutate
    };
}

export type ActionPlanFilters = {
    strategicPlanOnly: boolean
    pageSize?: number
    pageNumber?: number
    queryFilter?: string | null
    queryByFilter?: string | null
    statusFilter?: string | null
    sortFilter?: string | null
    sortOrderFilter?: string | null
    createdEndFilter?: string | null
    createdStartFilter?: string | null
    evaluationEndFilter?: string | null
    evaluationStartFilter?: string | null
    refreshKey?: string | null
    dashboardId?: number | string | null
    studentFilter?: string | null
    courseFilter?: string | null
    committeeFilter?: string | null
    labelsFilter?: string | null
    assigneeFilter?: string | null
    creatorFilter?: string | null
    strategicPlanFilter?: string | null
    strategicPlanRuuid?: string | null
}

export function useActionPlanById(id?: string | null) {
    const { data, error, isLoading, mutate } = useSWR<StandardResponse<Ticket>, Error>(
        id ? `${clientConfigs.baseUrl}/actionplans/${id}` : null
    );

    return {
        data: data?.data,
        error,
        isLoading,
        mutate,
    };
}

export function useActionPlans(
    {
        strategicPlanOnly,
        pageSize,
        pageNumber,
        queryFilter,
        queryByFilter,
        statusFilter,
        createdStartFilter,
        createdEndFilter,
        evaluationStartFilter,
        evaluationEndFilter,
        sortFilter,
        sortOrderFilter,
        refreshKey,
        dashboardId,
        studentFilter,
        courseFilter,
        committeeFilter,
        labelsFilter,
        assigneeFilter,
        creatorFilter,
        strategicPlanFilter,
        strategicPlanRuuid,
    }: ActionPlanFilters, fetch = true) {
    // build query params
    const params = new URLSearchParams();
    params.append("fields", "all");
    if (strategicPlanOnly) {
        params.append("strategicPlanOnly", "true");
    }
    if (pageSize) params.append("page_size", pageSize.toString());
    if (pageNumber) {
        params.append("page", pageNumber.toString());
    }

    if (queryFilter) {
        params.append("search_query", queryFilter);
    }
    if (queryByFilter) {
        params.append("search_by", queryByFilter);
    }
    if (statusFilter) {
        params.append("status", statusFilter);
    }

    // Apply default sorting when none is provided
    const hasSort = !!(sortFilter && sortFilter.trim());
    if (hasSort) {
        params.append("order_by", sortFilter as string);
        if (sortOrderFilter && sortOrderFilter.trim()) {
            params.append("order_by_direction", sortOrderFilter);
        }
    } else {
        params.append("order_by", "enflux_created");
        params.append("order_by_direction", (sortOrderFilter && sortOrderFilter.trim()) ? sortOrderFilter : "desc");
    }

    if (createdStartFilter) {
        params.append("created_start", createdStartFilter);
    }
    if (createdEndFilter) {
        params.append("created_end", createdEndFilter);
    }
    if (evaluationStartFilter) {
        params.append("evaluation_date_start", evaluationStartFilter);
    }
    if (evaluationEndFilter) {
        params.append("evaluation_date_end", evaluationEndFilter);
    }

    if (dashboardId !== undefined && dashboardId !== null && dashboardId !== "") {
        params.append("dashboard_id", String(dashboardId));
    }

    // Student filter (by student name)
    if (studentFilter && studentFilter.trim()) {
        params.append("student_ruuid", studentFilter);
    }

    // course filter (by course id)
    if (courseFilter && courseFilter.trim()) {
        params.append("course_id", courseFilter);
    }

    // committee filter (by committee id)
    if (committeeFilter && committeeFilter.trim()) {
        params.append("committee_id", committeeFilter);
    }

    // labels filter (by label id)
    if (labelsFilter && labelsFilter.trim()) {
        params.append("label_id", labelsFilter);
    }

    // assignee filter (by assignee ruuid)
    if (assigneeFilter && assigneeFilter.trim()) {
        params.append("assignee_ruuid", assigneeFilter);
    }

    // creator filter (by creator ruuid)
    if (creatorFilter && creatorFilter.trim()) {
        params.append("created_by", creatorFilter);
    }

    // strategic plan filter (by strategic plan item id)
    if (strategicPlanFilter && strategicPlanFilter.trim()) {
        params.append("strategic_plan_item_id", strategicPlanFilter);
    }

    // effectiveness plan drill-down (by strategic plan ruuid)
    if (strategicPlanRuuid?.trim()) {
        params.append("strategic_plan_ruuid", strategicPlanRuuid.trim());
    }

    const queryString = params.toString();
    const { data, error, isLoading, mutate } = useSWR<StandardResponse<Ticket[]>, Error>(fetch ? `${clientConfigs.baseUrl}/actionplans?${queryString}&refresh=${refreshKey}` : null);

    return {
        data: data,
        error,
        isLoading,
        mutate
    };
}
